import classNames from "classnames";
import "./Label.css";

type Props = {
    className?: string;
    children: React.ReactNode;
    disabled?: boolean;
    nowrap?: boolean;
};

type BaseLabel = (className?: string) => (props: Props) => React.JSX.Element;

function BaseLabel(baseClassName?: string) {
    return function Label(props: Props) {
        const { className, children, disabled, nowrap } = props;
        return (
            <span
                className={classNames(
                    "label",
                    baseClassName,
                    {
                        "label--disabled": disabled,
                        "label--nowrap": nowrap,
                    },
                    className,
                )}
            >
                {children}
            </span>
        );
    };
}

function Add(baseLabel: BaseLabel, className: string) {
    return (newClassName?: string) => baseLabel(classNames(className, newClassName));
}

function Curry(baseLabel: BaseLabel) {
    return (className?: string) => baseLabel(className);
}

const Colors = (baseComponent: BaseLabel) =>
    Object.assign(baseComponent(), {
        N10: baseComponent("label--n10"),
        N20: baseComponent("label--n20"),
        N50: baseComponent("label--n50"),
        N60: baseComponent("label--n60"),
        N100: baseComponent("label--n100"),
        P10: baseComponent("label--p10"),
        P30: baseComponent("label--p30"),
        P50: baseComponent("label--p50"),
        P60: baseComponent("label--p60"),
        P80: baseComponent("label--p80"),
        P90: baseComponent("label--p90"),
        P95: baseComponent("label--p95"),
        E40: baseComponent("label--e40"),
    });

const Weights = (baseComponent: BaseLabel) =>
    Object.assign(baseComponent(), {
        Normal: Colors(Add(baseComponent, "label--normal")),
        SemiBold: Colors(Add(baseComponent, "label--semibold")),
        Light: Colors(Add(baseComponent, "label--light")),
        ...Colors(Curry(baseComponent)),
    });

const Sizes = (baseComponent: BaseLabel) =>
    Object.assign(baseComponent(), {
        Large: Weights(Add(baseComponent, "label--large")),
        Medium: Weights(Add(baseComponent, "label--medium")),
        Small: Weights(Add(baseComponent, "label--small")),
        XSmall: Weights(Add(baseComponent, "label--xsmall")),
        XXSmall: Weights(Add(baseComponent, "label--xxsmall")),
    });

const Label = Sizes(BaseLabel);

export default Label;
